import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexGrid,
  ApexMarkers,
  ApexPlotOptions,
  ApexFill,
  ApexLegend,
  ApexStates,
  ApexTooltip,
  ApexYAxis,
  ApexNonAxisChartSeries,
} from 'ng-apexcharts';
import { ECurrencyCode } from './common.model';
import dayjs from 'dayjs';
import {IPriceMeasurement} from './products.model';

export interface ISummaryChartParams {
  currencyCode?: ECurrencyCode,
  fromDate?: dayjs.Dayjs,
  toDate?: dayjs.Dayjs,
  size?: number,
  priceMeasure?: IPriceMeasurement,
  clientId?: number,
}

export interface ISummaryChartOut {
  priceInTime: IChartPointValue[],
  depositsInTime: IChartPointValue[],
  moneyTransactionsInTime: IMoneyPoint[],
}

export enum EAvailableChartPeriod {
  ALL = 'ALL',
  YTD = 'YTD',
  Y5 = '5',
  Y3 = '3',
  Y1 = '1',
}

export enum EBackcastingChartPeriod {
  Y1 = 1,
  Y3 = 3,
  Y5 = 5,
  MAX = 'MAX',
}

export const INITIAL_CHART_PERIOD = EAvailableChartPeriod.ALL;

export interface IChartPointValue {
  x: number,
  y: number,
  depositValue?: number | null, // positive = deposit | negative = withdrawal
}

export interface IMoneyPoint {
  x: number,
  sumOfWithdrawals: number,
  sumOfDeposits: number,
}

export enum EProfitUnit {
  CZK = 'Kč',
  percent = '%',
}

export interface IHistoryChartInput {
  valuesInTime?: IChartPointValue[],
  deposits?: IChartPointValue[],
  percentsInTime?: IChartPointValue[],
};

export interface IHistoryChartOut {
  priceInTime: IChartPointValue[],
  depositsInTime: IChartPointValue[],
  moneyTransactionsInTime: IMoneyPoint[],
}

export interface IChartOptions  {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  fill: ApexFill;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  markers: ApexMarkers;
  plotOptions?: ApexPlotOptions;
};

export interface IPieChartOptions {
  series: ApexNonAxisChartSeries;
  labels: string[];
  colors: string[];
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  legend: ApexLegend;
  states: ApexStates;
  tooltip: ApexTooltip;
}

export interface ICompositionChartOptions{
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  colors: string[];
  states: ApexStates;
  fill: ApexFill;
}

export interface IChartTooltip {
  name: number | string,
  value: number | string,
  depositValue: number | string | null,
  isPercent: boolean | null,
  currency: ECurrencyCode,
}

export enum EBarChartFontColor {
  LIGHT = '#F3F8F8',
  DARK = '#28353A',
  DM_DARK = '#171F22',
  DM_LIGHT = '#E1EBEB',
}

export type BarColor =
  | '#821B1A'
  | '#BE1817'
  | '#F4403F'
  | '#FC796D'
  | '#FFB1A2'
  | '#FFD6C8'
  | '#F5F5F5'
  | '#E1EBEB'
  | '#6F9BA1'
  | '#486A72'
  | '#28353A'
  | '#0A0E0F'
  | '#470808';

export const barChartColorsMap = new Map<BarColor, EBarChartFontColor>([
  ['#821B1A', EBarChartFontColor.LIGHT],
  ['#BE1817', EBarChartFontColor.LIGHT],
  ['#F4403F', EBarChartFontColor.LIGHT],
  ['#FC796D', EBarChartFontColor.LIGHT],
  ['#FFB1A2', EBarChartFontColor.DARK],
  ['#FFD6C8', EBarChartFontColor.DARK],
  ['#F5F5F5', EBarChartFontColor.DARK],
  ['#E1EBEB', EBarChartFontColor.DARK],
  ['#6F9BA1', EBarChartFontColor.DARK],
]);

export const barChartColorsDarkModeMap = new Map<BarColor, EBarChartFontColor>([
  ['#FFB1A2', EBarChartFontColor.DM_DARK],
  ['#FC796D', EBarChartFontColor.DM_DARK],
  ['#F4403F', EBarChartFontColor.DM_DARK],
  ['#BE1817', EBarChartFontColor.DM_DARK],
  ['#821B1A', EBarChartFontColor.DM_LIGHT],
  ['#470808', EBarChartFontColor.DM_LIGHT],
  ['#0A0E0F', EBarChartFontColor.DM_LIGHT],
  ['#28353A', EBarChartFontColor.DM_LIGHT],
  ['#486A72', EBarChartFontColor.DM_LIGHT],
]);

export interface IInvestmentModelChartOut {
  [index: string]: IChartPointValue[];
  optimistic: IChartPointValue[],
  expected: IChartPointValue[],
  pessimistic: IChartPointValue[],
  pureInvestment: IChartPointValue[],
}

export interface IBackcastingChartOut {
  graph: IChartPointValue[],
  performances: {
    instrumentId: number,
    performance: number,
  }[],
  cumulativePriceAction?: number,
  periodInMonths: number,
  graphSize: number,
  maxPeriodInMonths: number,
}

export interface IInstrumentChartInput {
  instrumentId: number,
  fromDate?: string,
  size?: number,
}

export interface IInstrumentChartOut {
  priceInTime: IInstrumentChartPoint[],
  currency: string,
  oldestAvailableDate?: string,
}

export interface IInstrumentChartPoint {
  x: number,
  y: number,
}
